import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class JitsiScriptLoaderService {
  private loadingPromise: Promise<void> | null = null;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  load(domain: string): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.reject(
        new Error('JIT Meet solo puede inicializarse en el navegador.')
      );
    }

    if (window.JitsiMeetExternalAPI) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    const normalizedDomain = this.normalizeDomain(domain);
    const source = `${normalizedDomain}/external_api.js`;
    const existing = this.document.querySelector<HTMLScriptElement>(
      `script[data-jit-meet-sdk="true"]`
    );

    this.loadingPromise = new Promise<void>((resolve, reject) => {
      const script = existing ?? this.document.createElement('script');

      const onLoad = (): void => {
        cleanup();

        if (!window.JitsiMeetExternalAPI) {
          this.loadingPromise = null;
          reject(new Error('El SDK de Jitsi se cargó, pero no expuso su API.'));
          return;
        }

        resolve();
      };

      const onError = (): void => {
        cleanup();
        script.remove();
        this.loadingPromise = null;
        reject(new Error('No fue posible cargar el SDK de JIT Meet.'));
      };

      const cleanup = (): void => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (!existing) {
        script.src = source;
        script.async = true;
        script.dataset['jitMeetSdk'] = 'true';
        this.document.head.appendChild(script);
      }
    });

    return this.loadingPromise;
  }

  private normalizeDomain(domain: string): string {
    const trimmed = domain.trim().replace(/\/+$/, '');

    if (!trimmed) {
      throw new Error('El dominio de JIT Meet es obligatorio.');
    }

    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
}
